import React, { useEffect, useState } from "react";
import { t } from "i18next";

import { IButton, IHeader } from "../../../interfaces/event/header";

import Input from "../../shared/input/Input";
import BaseToggle from "../../shared/BaseToggle";
import InputColorPicker from "../../shared/inputColorPicker/InputColorPicker";
import PageTitle from "../../shared/pageTitle/PageTitle";

import { IFile, UploadImage } from "../../shared-v2/UploadImage";
import { Subtitle } from "../../shared-v2/Subtitle";
import { DeleteButton } from "../../shared-v2/DeleteButton";
import { Label } from "../../shared-v2/Label";
import { AddButton } from "../../shared-v2/AddButton";
import { InputModal } from "../../shared-v2/InputModal";

import { breakpointSize } from "../../../theme/mediaQuerie";
import { RowGrid, InputGroup, Row, Column} from './style';
import Button from "../../shared/button/Button";


interface IProps {
  header: IHeader;
  setHeader: React.Dispatch<React.SetStateAction<IHeader>>;
}

const isMobile = window.innerWidth <= breakpointSize.Sm;

export const Header = ({header, setHeader}: IProps) => {
  const [file, setFile] = useState<IFile | null>(null);
  const [showInputModal, setShowInputModal] = useState(false);
  const [newMenuItemTitle, setNewMenuItemTitle] = useState("");
  
  const updateHeader = (attr: keyof IHeader, value: string | Object) => {
    setHeader(prev => ({...prev, [attr]: value}));
  };

  const updateLogin = (attr: keyof IButton, value: string | boolean) => {
    setHeader(prev => ({...prev, login: {...prev.login, [attr]: value}}));
  }

  const updateRegister = (attr: keyof IButton, value: string | boolean) => {
    setHeader(prev => ({...prev, register: {...prev.register, [attr]: value}}));
  }

  const updateMenuItem = (attr: keyof IButton, value: string, index: number) => {
    let newMenuItens = header.menu.itens;
    newMenuItens[index] = {
      ...newMenuItens[index], 
      [attr]: value,
    };
    setHeader(prev => ({...prev, menu: {...prev.menu, itens: newMenuItens}}));
  }

  const deleteMenuItem = (index: number) => {
    let newMenuItens = header.menu.itens;
    newMenuItens.splice(index, 1);
    setHeader(prev => ({...prev, menu: {...prev.menu, itens: newMenuItens}}));
  }

  const addMenuItem = () => {
    setHeader(prev => ({...prev, menu: {...prev.menu, itens: [...prev.menu.itens, {
      title: newMenuItemTitle,
      color: "",
      background: "",
      disabled: false
    }]}}));
    setShowInputModal(false);
    setNewMenuItemTitle("");
  }

  return (
    <>
      { !isMobile && <PageTitle style={{margin: 0}}>{t("dashboard.event.header.title")}</PageTitle> }

      <RowGrid>
        <UploadImage 
          file={file} 
          maxImageSize={1024 * 1024 * 10} 
          setFile={(file) => {updateHeader("logo", file?.url ?? ""); setFile(file);}}
          title="Logo" 
          id="header-banner"
        />
        {!isMobile && (
           <InputGroup>
           <Label>{t("backgroundColor")}</Label>
           <InputColorPicker
             color={header.background}
             handleChangeColor={(e) => updateHeader("background", e.hex)}
           />
         </InputGroup>
        )}
      </RowGrid>

     <Column>
      <Row>
        <Subtitle>{t("buttonLogin")}</Subtitle>
        <BaseToggle 
          initialCheck={header.login.disabled ?? false} 
          onChange={(checked: boolean) => updateLogin("disabled", checked)} 
        />
      </Row>
      <RowGrid>
        <InputGroup>
          <Label>{t("title")}</Label>        
          <Input
              placeholder={t("dashboard.event.header.placeholder.login")!}
              value={header.login.title} 
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {updateLogin("title", e.target.value)}}
            />
        </InputGroup>
        <InputGroup>
          <Label>{t("textColor")}</Label>        
          <InputColorPicker
            color={header.login.color}
            handleChangeColor={(e) => updateLogin("color", e.hex)}
          />
        </InputGroup>
        <InputGroup>
          <Label>{t("buttonColor")}</Label>        
          <InputColorPicker
            color={header.login.background}
            handleChangeColor={(e) => updateLogin("background", e.hex)}
          />
        </InputGroup>
      </RowGrid>
     </Column>

      <Column>
        <Row>
          <Subtitle>{t("buttonRegister")}</Subtitle>
          <BaseToggle 
            initialCheck={header.register.disabled ?? false} 
            onChange={(checked: boolean) => updateRegister("disabled", checked)} 
          />
        </Row>
        <RowGrid>
          <InputGroup>
            <Label>{t("title")}</Label>       
            <Input
                placeholder={t("dashboard.event.header.placeholder.register")!}
                value={header.register.title} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {updateRegister("title", e.target.value)}}
              />
          </InputGroup>
          <InputGroup>
            <Label>{t("textColor")}</Label>        
            <InputColorPicker
              color={header.register.color}
              handleChangeColor={(e) => updateRegister("color", e.hex)}
            />
          </InputGroup>
          <InputGroup>
            <Label>{t("buttonColor")}</Label>        
            <InputColorPicker
              color={header.register.background}
              handleChangeColor={(e) => updateRegister("background", e.hex)}
            />
          </InputGroup>
        </RowGrid>
      </Column>

      {isMobile ? (
        <Button 
          rounded 
          fullWidth 
          size='small' 
          typographyType="headingXXS"
          enable={header.menu.itens.length !== 3} 
          onClick={() => setShowInputModal(true)}
          tooltip={header.menu.itens.length === 3 ? t("dashboard.event.header.tooltip.maxMenuItens")! : ""}
        >
          {t("createNewButton")}
        </Button>
      ) : (
        <AddButton 
          style={{marginLeft: "auto", marginBottom: "32px"}} 
          onAdd={() => setShowInputModal(true)} 
          disabled={header.menu.itens.length === 3}
          tooltip={header.menu.itens.length === 3 ? t("dashboard.event.header.tooltip.maxMenuItens")! : ""}
        />
      )}

      { header.menu.itens.map((item, index) => {
        return (
        <Column key={index}>
          <Row>
            <Subtitle>{header.menu.itens[index].title}</Subtitle>
            <DeleteButton onDelete={() => deleteMenuItem(index)} />
          </Row>
          <RowGrid>
            <InputGroup>
              <Label>Link</Label>        
              <Input
                placeholder="https://"
                value={header.menu.itens[index].url ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  updateMenuItem("url", e.target.value, index)}}
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("textColor")}</Label>        
              <InputColorPicker
                color={header.menu.itens[index].color}
                handleChangeColor={(e) => updateMenuItem("color", e.hex, index)}
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("buttonColor")}</Label>        
              <InputColorPicker
                color={header.menu.itens[index].background}
                handleChangeColor={(e) => updateMenuItem("background", e.hex, index)}
              />
            </InputGroup>
          </RowGrid>
        </Column>
      );})} 

      {isMobile && (
         <InputGroup>
         <Label>{t("backgroundColor")}</Label>
         <InputColorPicker
           color={header.background}
           handleChangeColor={(e) => updateHeader("background", e.hex)}
         />
       </InputGroup>
      )}

      <InputModal 
        onConfirm={addMenuItem} 
        onCancel={() => {setShowInputModal(false); setNewMenuItemTitle("");}}
        setShowModal={setShowInputModal} 
        showModal={showInputModal} 
        setValue={setNewMenuItemTitle}  
        value={newMenuItemTitle}
        title={t("newButton")}
        inputRequired
        label={t("dashboard.event.header.newButtonLabel")}
      />
    </>
  );
}